import React from 'react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { color } from '@/themes/color';

export type FAQCategory =
  | 'about'
  | 'using'
  | 'visiting'
  | 'legal'
  | 'technical'
  | 'privacy'
  | 'owners';

export interface FAQItem {
  question: string;
  answer: string | React.ReactNode;
  answerText?: string;
  category: FAQCategory;
}

export const FAQ_ITEMS: FAQItem[] = [
  {
    question: 'What is the UK Vineyard Guide app and what does it do?',
    answer: (
      <>
        UK Vineyard Guide app connects visitors with vineyards across England
        and Wales whether you're planning a weekend away, a day out with
        friends, or just want to know what's growing near you. Here's what's in
        the app:
        <Box component="ul" sx={{ mt: '8px', mb: 0, pl: '20px' }}>
          <Box component="li">
            <strong style={{ fontWeight: '350' }}>Discover</strong> — Hundreds
            of vineyards across England and Wales, all on one interactive
            colour-coded map
          </Box>
          <Box component="li">
            <strong style={{ fontWeight: '350' }}>Plan</strong> — Everything you
            need to plan the perfect vineyard visit, from tours and tastings to
            full itineraries
          </Box>
          <Box component="li">
            <strong style={{ fontWeight: '350' }}>Experience</strong> — Make
            memories at some of the most beautiful wine estates in the country
          </Box>
        </Box>
      </>
    ),
    answerText:
      "UK Vineyard Guide app connects visitors with vineyards across England and Wales whether you're planning a weekend away, a day out with friends, or just want to know what's growing near you. Here's what's in the app: Discover — Hundreds of vineyards across England and Wales, all on one interactive colour-coded map. Plan — Everything you need to plan the perfect vineyard visit, from tours and tastings to full itineraries. Experience — Make memories at some of the most beautiful wine estates in the country.",
    category: 'about',
  },
  {
    question: 'Is the app free to use?',
    answer: 'Yes, UK Vineyard Guide is a free to use mobile app.',
    category: 'about',
  },
  {
    question: 'Which regions and vineyards are covered?',
    answer:
      'We currently cover all regions in England and Wales, and are looking to include regions in Scotland and Northern Ireland in the future.',
    category: 'about',
  },
  {
    question: 'How often is the directory updated?',
    answer:
      'The directory is updated on an ongoing basis, whenever we receive new information, or uncover a new vineyard to be added to the app.',
    category: 'about',
  },
  {
    question: 'Which devices is the app available on?',
    answer: 'UK Vineyard Guide is available on iOS and Android.',
    category: 'about',
  },
  {
    question: 'How do I search for vineyards near me?',
    answer: (
      <>
        Select the Explore section of the app, then:
        <Box component="ul" sx={{ mt: '8px', mb: 0, pl: '20px' }}>
          <Box component="li">
            Zoom in on the region you want to explore to see individual
            vineyards on the map
          </Box>
          <Box component="li">Search by Vineyard Name</Box>
          <Box component="li">Filter by Region, Distance or Amenities</Box>
        </Box>
      </>
    ),
    answerText:
      'To search for local vineyards select the Explore section of the app. You can then zoom in on the region you want to look at to see individual vineyards on the map. You can also search by Vineyard Name or filter by Region, Distance or Amenities.',
    category: 'using',
  },
  {
    question: 'Can I filter by region, grape variety, or visit type?',
    answer:
      'Yes, our interactive map enables you to filter by Region, Distance, Amenities, Vineyard Name and even by what is open right now.',
    category: 'using',
  },
  {
    question: 'Are all UK vineyards listed, or only certain ones?',
    answer:
      "We are currently building out the list which will eventually contain every vineyard in the UK. The app features a comprehensive list of vineyards focused on those you can visit, and is being continually updated. The next stage of the app will showcase winery locations (wine producers who don't have vineyards or a cellar door) connecting you with places to buy their wines.",
    category: 'using',
  },
  {
    question: "What if a vineyard I know isn't listed?",
    answer:
      "If a vineyard isn't listed please get in touch with us at hello@ukvineyardguide.co.uk and we will add it to the directory.",
    category: 'using',
  },
  {
    question: 'How can I get my vineyard listed?',
    answer: (
      <>
        Please drop us a line at{' '}
        <Link
          href="mailto:hello@ukvineyardguide.co.uk"
          underline="none"
          sx={{ color: color.terracotta.default }}
        >
          hello@ukvineyardguide.co.uk
        </Link>{' '}
        with details of your vineyard to be added to UK Vineyard Guide. A member
        of the team will be in touch to ensure we have all the information
        needed to include your vineyard.
      </>
    ),
    answerText:
      'Please drop us a line at hello@ukvineyardguide.co.uk with details of your vineyard to be added to UK Vineyard Guide. A member of the team will be in touch to ensure we have all the information needed to include your vineyard.',
    category: 'owners',
  },
  {
    question: "How do I update my vineyard's information?",
    answer: (
      <>
        If your vineyard information in the app needs to be updated please get
        in touch at{' '}
        <Link
          href="mailto:hello@ukvineyardguide.co.uk"
          underline="none"
          sx={{ color: color.terracotta.default }}
        >
          hello@ukvineyardguide.co.uk
        </Link>{' '}
        with 'Vineyard Info Update' as the subject line and our team will then
        make sure your latest info is included in the app.
      </>
    ),
    answerText:
      "If your vineyard information in the app needs to be updated please get in touch at hello@ukvineyardguide.co.uk with 'Vineyard Info Update' as the subject line and our team will then make sure your latest info is included in the app.",
    category: 'owners',
  },
  {
    question: 'Is there a cost to be listed?',
    answer: (
      <>
        All vineyards are included in UK Vineyard Guide for free. We also offer
        paid packages to enhance profile listings, booking links and priority
        placement plus editorial and media coverage. If you are interested in
        our vineyard packages please get in touch at{' '}
        <Link
          href="mailto:hello@ukvineyardguide.co.uk"
          underline="none"
          sx={{ color: color.terracotta.default }}
        >
          hello@ukvineyardguide.co.uk
        </Link>
        .
      </>
    ),
    answerText:
      'All vineyards are included in UK Vineyard Guide for free. We also offer paid packages to enhance profile listings, booking links and priority placement plus editorial and media coverage. If you are interested in our vineyard packages please get in touch at hello@ukvineyardguide.co.uk.',
    category: 'owners',
  },
  {
    question: 'Do I need to book ahead, or can I turn up?',
    answer:
      'Every vineyard is different. Take a look in the individual Vineyard page within the app, which will detail whether bookings are required. UK Vineyards can get very busy when the weather warms up, so if in doubt do call or email the vineyard before visiting - you can find contact details in their profile on the app.',
    category: 'visiting',
  },
  {
    question: 'How do I know if a vineyard is open?',
    answer:
      'You can find details of Opening Days / Times on the individual vineyard profile pages within the app.',
    category: 'visiting',
  },
  {
    question: 'Are opening times and seasonal closures kept up to date?',
    answer:
      'We do our best to keep opening times and seasonal closures as current as possible, working closely with vineyards to keep their listings accurate, and our team carries out regular checks too. However, hours can change at short notice, especially around harvest season and bank holidays, so we always recommend confirming directly with the vineyard before you visit.',
    category: 'visiting',
  },
  {
    question: 'Do I need to be 18+ to use the app?',
    answer:
      'Yes. As a directory focused on wine and vineyards, this app is intended for users aged 18 and over in line with UK alcohol promotion guidelines.',
    category: 'legal',
  },
  {
    question: 'Why do you ask for my age?',
    answer:
      'Wine is at the heart of everything we do, so this app is intended for adults aged 18+, the legal drinking age in the UK. We include this check to make sure our content reaches the right audience and to comply with responsible alcohol promotion guidelines.',
    category: 'legal',
  },
  {
    question: 'Does the app require an account to use?',
    answer:
      'Yes, you will need to register an account to access the app. This means you will be able to save your favourite vineyards, get personalised recommendations and plan vineyard visits.',
    category: 'technical',
  },
  {
    question: 'Does the app work offline or in areas with poor signal?',
    answer:
      'As long as you have downloaded the app whilst you have wifi or strong signal, then yes the app will work offline. The reason we developed UK Vineyard Guide as an app was to ensure all the information can be accessed wherever you are, especially in rural areas with poor signal or locations with no wifi.',
    category: 'technical',
  },
  {
    question: 'How do I report an error or incorrect listing?',
    answer: (
      <>
        Please get in touch at{' '}
        <Link
          href="mailto:hello@ukvineyardguide.co.uk"
          underline="none"
          sx={{ color: color.terracotta.default }}
        >
          hello@ukvineyardguide.co.uk
        </Link>{' '}
        with as much detail as possible if you spot an error or incorrect
        listing and our team will work to rectify this.
      </>
    ),
    answerText:
      'Please get in touch at hello@ukvineyardguide.co.uk with as much detail as possible if you spot an error or incorrect listing and our team will work to rectify this.',
    category: 'technical',
  },
  {
    question: 'How do I delete my account?',
    answer:
      'You can delete your account from the Profile section of the app. Select Privacy & Security, then scroll to Account Actions where you will find the option to delete your account.',
    category: 'technical',
  },
  {
    question: 'Can I leave reviews or ratings?',
    answer: 'Currently you are able to leave ratings.',
    category: 'technical',
  },
  {
    question: 'What data do you collect?',
    answer: (
      <>
        You can find full details of our{' '}
        <Link
          href="/UK%20Vineyard%20-%20Privacy%20Policy.pdf"
          target="_blank"
          rel="noopener noreferrer"
          underline="none"
          sx={{ color: color.terracotta.default }}
        >
          privacy policy
        </Link>{' '}
        including what data we collect and process.
      </>
    ),
    answerText:
      'You can find full details of our privacy policy including what data we collect and process.',
    category: 'privacy',
  },
  {
    question: 'Do you share my location data?',
    answer: 'No we do not share your location data.',
    category: 'privacy',
  },
];

export const CATEGORIES: Array<{ id: FAQCategory; label: string }> = [
  { id: 'about', label: 'About the UK Vineyard Guide App' },
  { id: 'using', label: 'Exploring & Features' },
  { id: 'visiting', label: 'Planning Your Visit' },
  { id: 'owners', label: 'For Vineyard Owners' },
  { id: 'technical', label: 'Account & Support' },
  { id: 'legal', label: 'Age & Legal' },
  { id: 'privacy', label: 'Privacy Policy' },
];

export const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_ITEMS.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text:
        item.answerText ?? (typeof item.answer === 'string' ? item.answer : ''),
    },
  })),
};

export const FAQ_URL = 'https://www.ukvineyardguide.co.uk/faq';
